import { TrackingPixel } from "@karbonhq/marketing-shared-utils/components/TrackingPixel"
import {
  fetchClearbitData,
  filterClearbitData,
} from "@karbonhq/marketing-shared-utils/hooks/useClearbitEnrichment"
import { salesforceCountries } from "@karbonhq/marketing-shared-utils/utils/countries"
import isGDPR from "@karbonhq/marketing-shared-utils/utils/IsGDPR"
import { capitalise } from "@karbonhq/marketing-shared-utils/utils/Strings"
import { UTMFields } from "@karbonhq/marketing-shared-utils/utils/utmFields"
import { track } from "@src/utils/analytics"
import { Formik } from "formik"
import { get } from "lodash"
import PropTypes from "prop-types"
import React, { useContext, useState } from "react"
import { css } from "styled-components"
import { Margin, mt } from "styled-components-spacing"
import { object, string } from "yup"

import { getLambdaRoute } from "../../utils/getLambdaRoute"
import { AnimatedTick } from "../atoms/AnimatedTick"
import { Button } from "../atoms/Buttons"
import { P } from "../atoms/Elements"
import { Input, Label, RadioLabel, ValidationError } from "../atoms/FormFields"
import { H2 } from "../atoms/Headings"
import { Link } from "../atoms/Link"
import { Lines } from "../atoms/Spinners"
import { Form, FormContainer, VisibleBlock } from "../FormContainer"
import { VisitorLocaleContext } from "../providers/VisitorLocaleProvider"
import { updateCTFTemplate } from "./updateCTFTemplate"

export const DownloadableResourceForm = props => {
  const localeData = useContext(VisitorLocaleContext)

  const formStates = {
    FORM: "form",
    SUBMITTING: "submitting",
    SUBMITTED: "submitted",
  }
  const [formState, setFormState] = useState(formStates.FORM) // form, submitting, submitted

  const handleSubmit = async values => {
    const { Bot, ...data } = values

    if (Bot) {
      handleSuccess()
      return
    }

    setFormState(formStates.SUBMITTING)

    updateCTFTemplate({
      contentfulId: props.contentfulId,
      incrementDownload: true,
    })

    track(`WE-TemplateDownloaded`, {
      label: props.fileName,
    })
    let enrichment = await fetchClearbitData(data.Email)
    enrichment = filterClearbitData(enrichment.data)

    data.Company = get(enrichment, "data.company.name")
    data.Country = get(salesforceCountries, localeData.countryCode)
    data.Employees = get(enrichment, "data.company.metrics.employees")

    const configObj = {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ ...data, formURL: props.formUrl, ...UTMFields() }),
    }

    fetch(getLambdaRoute(`pardot-proxy`), configObj)
      .catch(error => {
        console.log(error)
      })
      .finally(handleSuccess)
  }

  const handleSuccess = response => {
    setFormState(formStates.SUBMITTED)
    props.incrementDownloads && props.incrementDownloads()
    if (window) {
      setTimeout(() => window.open(props.fileUrl), 1000)
      // window.open(props.fileUrl)
    }
  }

  const validationSchema = object().shape({
    FirstName: string().required("First name required"),
    LastName: string().required("Last name required"),
    Email: string().email("Invalid email").required("Email address required"),
    MarketingOptin: isGDPR(localeData.countryCode)
      ? string().required("Please either opt in or out.")
      : string(),
  })

  let firstName = ""
  let lastName = ""
  if (localeData?.karbonData?.data?.displayName) {
    ;[firstName, ...lastName] =
      localeData.karbonData.data.displayName.split(" ")
    lastName = lastName.join(" ")
  }

  const intialValues = {
    FirstName: firstName,
    LastName: lastName,
    Country: get(salesforceCountries, localeData.countryCode, ""),
    // Country: get(salesforceCountries, localeData.countryCode, ""),
    Email: localeData?.karbonData?.data?.emailAddress
      ? localeData.karbonData.data.emailAddress
      : "",
    Bot: "",
    MarketingOptin: "",
  }

  return (
    <>
      {/* LinkedIn Tracking Pixel added for template download */}
      {formState === formStates.SUBMITTED && (
        <TrackingPixel src="https://px.ads.linkedin.com/collect/?pid=3239393&conversionId=3903996&fmt=gif" />
      )}
      <FormContainer>
        <Formik
          enableReinitialize
          onSubmit={handleSubmit}
          initialValues={intialValues}
          validationSchema={validationSchema}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            touched,
            setFieldValue,
            submitForm,
          }) => {
            if (
              !Object.keys(touched).length &&
              values.FirstName.length &&
              values.LastName.length &&
              values.Email.length &&
              !isGDPR(values.Country)
            ) {
              // console.log("programatic submit possible.")
              submitForm()
            }

            return (
              <Form visible={formState === formStates.FORM} name="contact">
                <H2 mb={4} centered>
                  Download template
                </H2>
                <p hidden>
                  <label>
                    <input
                      name="bot"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.bot}
                    />
                  </label>
                </p>

                <div
                  css={css`
                    display: flex;
                  `}
                >
                  <Label htmlFor="DLFirstName">
                    First Name
                    <Input
                      left
                      name="FirstName"
                      type="text"
                      // onChange={handleChange}
                      onChange={e => {
                        setFieldValue("FirstName", capitalise(e.target.value))
                      }}
                      onBlur={handleBlur}
                      value={values.FirstName}
                      error={errors.FirstName && touched.FirstName}
                    />
                    {errors.FirstName && touched.FirstName ? (
                      <ValidationError>{errors.FirstName}</ValidationError>
                    ) : null}
                  </Label>

                  <Label htmlFor="DLLastName">
                    Last Name
                    <Input
                      right
                      name="LastName"
                      type="text"
                      // onChange={handleChange}
                      onChange={e => {
                        setFieldValue("LastName", capitalise(e.target.value))
                      }}
                      onBlur={handleBlur}
                      value={values.LastName}
                      error={errors.LastName && touched.LastName}
                    />
                    {errors.LastName && touched.LastName ? (
                      <ValidationError>{errors.LastName}</ValidationError>
                    ) : null}
                  </Label>
                </div>

                <Label htmlFor="DLEmail">
                  Email
                  <Input
                    name="Email"
                    type="email"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    value={values.Email}
                    error={errors.Email && touched.Email}
                  />
                  {errors.Email && touched.Email ? (
                    <ValidationError>{errors.Email}</ValidationError>
                  ) : null}
                </Label>

                {/* GDPR */}

                {isGDPR(localeData.countryCode) && (
                  <div
                    css={css`
                      font-size: 16px;
                      text-align: left;
                    `}
                  >
                    <Margin bottom={1}>
                      <P mb={1}>
                        I am happy to be contacted about Karbon products, events
                        and resources.
                      </P>
                      <RadioLabel noCaps>
                        <input
                          type="radio"
                          name="MarketingOptin"
                          value="opt-in"
                          checked={values.MarketingOptin === "opt-in"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        Opt In
                      </RadioLabel>
                      <RadioLabel noCaps>
                        <input
                          type="radio"
                          name="MarketingOptin"
                          value="opt-out"
                          checked={values.MarketingOptin === "opt-out"}
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                        Miss Out
                      </RadioLabel>
                    </Margin>

                    {errors.MarketingOptin && touched.MarketingOptin ? (
                      <ValidationError>{errors.MarketingOptin}</ValidationError>
                    ) : null}
                  </div>
                )}

                <div
                  css={css`
                    ${mt(3)};
                    text-align: center;
                  `}
                >
                  <Button size={2} onClick={handleSubmit} type="submit">
                    Download
                  </Button>
                </div>
              </Form>
            )
          }}
        </Formik>
        <VisibleBlock visible={formState === formStates.SUBMITTING}>
          <Lines />
        </VisibleBlock>

        <VisibleBlock visible={formState === formStates.SUBMITTED}>
          <div
            css={css`
              max-width: 368px;
              margin: 0 auto;
            `}
          >
            <Margin bottom={5} top={1}>
              {/* Don't mount till visible or we get no animation */}
              {formState === formStates.SUBMITTED && (
                <AnimatedTick width={30} />
              )}
              <H2>It's all yours!</H2>
              If the download doesn't automatically start, you can{" "}
              <Link underlined target="_blank" href={props.fileUrl}>
                download now
              </Link>
              . A copy has also been sent to your inbox.
            </Margin>

            {props.hideModal && (
              <Button
                size={2}
                onClick={() => {
                  props.hideModal()
                }}
              >
                Done
              </Button>
            )}
          </div>
        </VisibleBlock>
      </FormContainer>
    </>
  )
}

DownloadableResourceForm.propTypes = {
  formUrl: PropTypes.string.isRequired,
  fileUrl: PropTypes.string.isRequired,
  fileName: PropTypes.string.isRequired,
  incrementDownloads: PropTypes.func,
  contentfulId: PropTypes.string.isRequired,
  linkUrl: PropTypes.string.isRequired,
  showModal: PropTypes.func,
  hideModal: PropTypes.func,
}
